import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { supabase } from '../supabaseClient.ts';

type YearRow = { id: string; year: string };
type SubjectRow = { id: string; name: string };

type Props = {
  yearId: string;
  subjectIds: string[];
  onYearChange: (yearId: string) => void;
  onToggleSubject: (subjectId: string) => void;
  onError: (message: string | null) => void;
};

/**
 * Year + subject pickers backed by the global curriculum tables. Shared by
 * email signup and the post-Google finish step so the two forms stay in sync.
 */
const CurriculumFields: React.FC<Props> = ({
  yearId,
  subjectIds,
  onYearChange,
  onToggleSubject,
  onError,
}) => {
  const [years, setYears] = useState<YearRow[]>([]);
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    (async () => {
      const { data, error: yearErr } = await supabase
        .from('years')
        .select('id, year')
        .order('year', { ascending: true });

      if (yearErr) {
        onErrorRef.current(`Could not load year levels: ${yearErr.message}`);
        return;
      }

      const yearRows = (data ?? []) as YearRow[];
      if (yearRows.length === 0) {
        onErrorRef.current('No year levels are available to choose from yet.');
      }
      setYears(yearRows);
    })();
  }, []);

  useEffect(() => {
    if (!yearId) {
      setSubjects([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: subjectErr } = await supabase
        .from('subjects')
        .select('id, name')
        .eq('year_id', yearId)
        .order('name', { ascending: true });

      if (cancelled) return;
      if (subjectErr) {
        onErrorRef.current(`Could not load subjects: ${subjectErr.message}`);
        return;
      }
      setSubjects((data ?? []) as SubjectRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [yearId]);

  return (
    <>
      <TextField
        select
        label="Year level"
        value={yearId}
        onChange={(e) => onYearChange(e.target.value)}
        fullWidth
        required
      >
        {years.length === 0 ? (
          <MenuItem value="" disabled>
            No year levels available
          </MenuItem>
        ) : (
          years.map((y) => (
            <MenuItem key={y.id} value={y.id}>
              {y.year}
            </MenuItem>
          ))
        )}
      </TextField>

      {yearId && (
        <Box>
          <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
            Subjects
          </FormLabel>
          {subjects.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No subjects available for that year yet.
            </Typography>
          ) : (
            <FormGroup
              sx={{
                mt: 0.5,
                px: 1.5,
                py: 0.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 180,
                overflowY: 'auto',
              }}
            >
              {subjects.map((s) => (
                <FormControlLabel
                  key={s.id}
                  control={
                    <Checkbox
                      checked={subjectIds.includes(s.id)}
                      onChange={() => onToggleSubject(s.id)}
                      size="small"
                    />
                  }
                  label={s.name}
                />
              ))}
            </FormGroup>
          )}
        </Box>
      )}
    </>
  );
};

export default CurriculumFields;
